import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AdminUsers.module.css";
import useAuthGuard from "../../../hooks/useAuthGuard";
import {
  listAdminsApi,
  updateAdminApi,
  deleteAdminApi,
} from "../../../api/api";

const roles = [
  { value: "staff", label: "Staff" },
  { value: "admin", label: "Admin" },
  { value: "superadmin", label: "Super Admin" },
  { value: "stringer", label: "Stringer" },
];

const AdminUsers = () => {
  useAuthGuard();
  const [role, setRole] = useState("");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const r = localStorage.getItem("adminRole");
    setRole(r || "");
    const token = localStorage.getItem("adminToken");
    listAdminsApi(token)
      .then((res) => setRows(res.data))
      .catch((e) => console.error(e));
  }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter(
      (r) =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.role || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  // Admins see everyone except superadmins, can edit staff and stringers only,
  // and cannot delete anyone
  const isSuperadmin = role === "superadmin";
  const canEdit = (u) => isSuperadmin || ["staff", "stringer"].includes((u.role || "").toLowerCase());
  const editableRoles = isSuperadmin
    ? roles
    : roles.filter((r) => r.value === "staff" || r.value === "stringer");

  if (role && role !== "superadmin" && role !== "admin") {
    return (
      <div className={styles.full}>
        <header className={styles.header}>
          <h2 className={styles.pageHeading}>Access Denied</h2>
        </header>
        <div className={styles.main}>
          <div className={styles.card}>
            <p>You must be an Admin or Super Admin to manage users.</p>
            <button className={styles.goback} onClick={() => navigate(-1)}>
              &larr; Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const openEdit = (u) => {
    setEditing({
      user_id: u.user_id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      role: (u.role || "").toLowerCase(),
      location: u.location || "",
    });
    setMsg({ type: "", text: "" });
  };

  const saveEdit = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      await updateAdminApi({
        userId: editing.user_id,
        payload: {
          firstName: editing.first_name,
          lastName: editing.last_name,
          email: editing.email,
          role: editing.role,
          location: editing.role === "stringer" ? editing.location : null,
        },
        token,
      });
      setRows((old) =>
        old.map((r) =>
          r.user_id === editing.user_id ? { ...r, ...editing } : r
        )
      );
      setEditing(null);
      setMsg({ type: "success", text: "User updated." });
    } catch (e) {
      const apiMsg = e?.response?.data?.error || "Update failed.";
      setMsg({ type: "error", text: apiMsg });
    }
  };

  const delUser = async (u) => {
    setDeleteTarget(u);
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget) return;
    const token = localStorage.getItem("adminToken");
    try {
      await deleteAdminApi({ userId: deleteTarget.user_id, token });
      setRows((old) => old.filter((r) => r.user_id !== deleteTarget.user_id));
      setMsg({ type: "success", text: "User deleted." });
    } catch (e) {
      const apiMsg = e?.response?.data?.error || "Delete failed.";
      setMsg({ type: "error", text: apiMsg });
    } finally {
      setDeleteTarget(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const options = { day: "numeric", month: "short", year: "numeric" };
      return new Date(dateString).toLocaleDateString("en-GB", options);
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "Invalid Date";
    }
  };

  return (
    <div className={styles.full}>
      <header className={styles.header}>
        <h1 className={styles.pageHeading}>USERS</h1>
      </header>

      <main className={styles.main}>
        <div className={styles.approvalContainer}>
          {msg.text && (
            <p
              className={
                msg.type === "error"
                  ? styles.errorMessage
                  : styles.successMessage
              }
            >
              {msg.text}
            </p>
          )}

          <div className={styles.filterBar}>
            <input
              placeholder="Search name, email, role…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={styles.input}
            />
            <button className={styles.goback} onClick={() => navigate(-1)}>
              &larr; Back
            </button>
          </div>

          <table className={styles.requestsTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Location</th>
                <th>Status</th>
                <th>Date Created</th>
                <th>Last Updated</th>
                <th className={styles.editColHeader}>Edit</th>
                {isSuperadmin && <th className={styles.deleteColHeader}>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, idx) => (
                <tr key={u.user_id}>
                  <td data-th="#"> {idx + 1} </td>
                  <td data-th="Name">
                    {u.first_name} {u.last_name}
                  </td>
                  <td data-th="Email">{u.email}</td>
                  <td data-th="Role">{u.role}</td>
                  <td data-th="Location">{u.location || "-"}</td>
                  <td data-th="Status">{u.status}</td>
                  <td data-th="Date Created">{formatDate(u.date_created)}</td>
                  <td data-th="Last Updated">
                    {formatDate(u.last_updated_at)}
                  </td>

                  <td data-th="Edit">
                    {canEdit(u) ? (
                      <button
                        className={styles.approveButton}
                        onClick={() => openEdit(u)}
                      >
                        Edit
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>

                  {isSuperadmin && (
                    <td data-th="Delete">
                      <button
                        className={styles.rejectButton}
                        onClick={() => delUser(u)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isSuperadmin ? 10 : 9} style={{ textAlign: "center", padding: 20 }}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {deleteTarget && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3>Delete User</h3>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {deleteTarget.first_name} {deleteTarget.last_name}
                  </strong>
                  ?
                </p>
                <div className={styles.actions}>
                  <button
                    className={styles.rejectButton}
                    onClick={confirmDeleteUser}
                  >
                    Confirm Delete
                  </button>
                  <button
                    className={styles.approveButton}
                    onClick={() => setDeleteTarget(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {editing && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3>Edit User</h3>
                <div className={styles.formRow}>
                  <label>First Name</label>
                  <input
                    value={editing.first_name}
                    onChange={(e) =>
                      setEditing((x) => ({ ...x, first_name: e.target.value }))
                    }
                  />
                </div>
                <div className={styles.formRow}>
                  <label>Last Name</label>
                  <input
                    value={editing.last_name}
                    onChange={(e) =>
                      setEditing((x) => ({ ...x, last_name: e.target.value }))
                    }
                  />
                </div>
                <div className={styles.formRow}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={editing.email}
                    onChange={(e) =>
                      setEditing((x) => ({ ...x, email: e.target.value }))
                    }
                  />
                </div>
                <div className={styles.formRow}>
                  <label>Role</label>
                  <select
                    value={editing.role ? editing.role.toLowerCase() : ""}
                    onChange={(e) =>
                      setEditing((x) => ({ ...x, role: e.target.value }))
                    }
                  >
                    {editableRoles.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                {(editing.role || "").toLowerCase() === "stringer" && (
                  <div className={styles.formRow}>
                    <label>Location *</label>
                    <select
                      value={editing.location || ""}
                      onChange={(e) =>
                        setEditing((x) => ({ ...x, location: e.target.value }))
                      }
                      required
                    >
                      <option value="">Select Location</option>
                      <option value="Jumeirah Store">Jumeirah Store</option>
                      <option value="Marina Store">Marina Store</option>
                      <option value="Dubai Hills Store">Dubai Hills Store</option>
                    </select>
                  </div>
                )}
                <div className={styles.actions}>
                  <button className={styles.approveButton} onClick={saveEdit}>
                    Save
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={() => setEditing(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;
